import { useCallback, useState } from "react";
import type { TelemetryDataPoint } from "@/types";

interface DragStart {
  x: number;
  domain: [number, number];
}

interface UseChartPanZoomParams {
  zoomDomain: [number, number] | null;
  onZoomChange: (domain: [number, number] | null) => void;
  data: TelemetryDataPoint[];
  chartWidth: number;
  onManualPan?: () => void;
  syncCurrentTime?: number;
  autoCenter?: boolean;
  onAutoCenterChange?: (enabled: boolean) => void;
}

interface UseChartPanZoomResult {
  isDragging: boolean;
  realTimeDomain: [number, number] | null;
  beginPan: (mouseX: number) => void;
  handlePanMove: (mouseX: number, isPointerDown: boolean) => boolean;
  endPan: () => void;
  cancelPan: () => void;
}

// Pan state machine: left-button drag while zoomed shifts the visible time
// domain. `realTimeDomain` mirrors the in-flight pan so callers can render
// smoothly before the parent zoom state round-trips.
export const useChartPanZoom = ({
  zoomDomain,
  onZoomChange,
  data,
  chartWidth,
  onManualPan,
  syncCurrentTime,
  autoCenter,
  onAutoCenterChange,
}: UseChartPanZoomParams): UseChartPanZoomResult => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<DragStart | null>(null);
  const [realTimeDomain, setRealTimeDomain] = useState<[number, number] | null>(
    null,
  );

  const beginPan = useCallback(
    (mouseX: number) => {
      if (!zoomDomain) return;
      setIsDragging(true);
      setDragStart({ x: mouseX, domain: zoomDomain });
    },
    [zoomDomain],
  );

  // Returns true when the event was consumed by the pan gesture.
  const handlePanMove = useCallback(
    (mouseX: number, isPointerDown: boolean): boolean => {
      if (!(isDragging && dragStart && zoomDomain)) return false;

      // Check if the pointer is still pressed
      if (!isPointerDown) {
        // Pointer is no longer pressed, stop panning
        setIsDragging(false);
        setDragStart(null);
        setRealTimeDomain(null);
        return true;
      }

      const deltaX = mouseX - dragStart.x;
      const timeRange = dragStart.domain[1] - dragStart.domain[0];
      const deltaTime = (-deltaX / chartWidth) * timeRange;

      // Calculate new domain
      let newStart = dragStart.domain[0] + deltaTime;
      let newEnd = dragStart.domain[1] + deltaTime;

      // Constrain to data bounds
      const dataMin = Math.min(...data.map((d) => d.timestamp));
      const dataMax = Math.max(...data.map((d) => d.timestamp));

      if (newStart < dataMin) {
        newStart = dataMin;
        newEnd = dataMin + timeRange;
      }
      if (newEnd > dataMax) {
        newEnd = dataMax;
        newStart = dataMax - timeRange;
      }

      // Update real-time domain for smooth note marker movement
      setRealTimeDomain([newStart, newEnd]);

      onZoomChange([newStart, newEnd]);
      onManualPan?.(); // Notify that chart was manually panned

      // Check if video point is still visible after panning
      if (syncCurrentTime !== undefined && autoCenter && onAutoCenterChange) {
        // Use a timeout to check visibility after the zoom change has been applied
        setTimeout(() => {
          // Check if time is visible in the current domain
          if (syncCurrentTime < newStart || syncCurrentTime > newEnd) {
            onAutoCenterChange(false);
          }
        }, 50);
      }
      return true;
    },
    [
      isDragging,
      dragStart,
      zoomDomain,
      chartWidth,
      data,
      onZoomChange,
      onManualPan,
      syncCurrentTime,
      autoCenter,
      onAutoCenterChange,
    ],
  );

  // Full pan reset, including the in-flight real-time domain.
  const endPan = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
    setRealTimeDomain(null);
  }, []);

  // Clear only the gesture state; keeps the current real-time domain.
  const cancelPan = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  return {
    isDragging,
    realTimeDomain,
    beginPan,
    handlePanMove,
    endPan,
    cancelPan,
  };
};
